// The pre-join screen (BRIEF 6.3): whose call it is, a self preview, mic and
// camera toggles, a settings button, the name, and one Join button. It fits
// the screen with no scrolling on a phone or a laptop (Adam, 2026-09-28:
// "don't make them have to scroll"); on a wide screen the preview is a
// landscape frame as big as the window allows, not a phone-shaped one.
//
// Camera and mic are asked for together when the page opens, whatever the
// toggles say, so the browser asks once. What the preview shows while it
// waits, and when the answer is no, is spelled out rather than left black.
//
// Background blur is settled here before the call begins (Adam, 2026-10-04:
// "background blur should be doable before meeting begins"), from Settings
// rather than the bar ("make blur live under settings before joining"), and the
// preview shows it. The choice is the same stored preference the call reads
// when it opens the camera. Settings also holds the color and the theme, the
// same two the account Settings page keeps.

import { useEffect, useRef, useState, type JSX } from "react";
import { APPEARANCES, type AccentHue, type Appearance } from "@breeze/core";
import { applyAppearance, getStoredAppearance, setStoredAppearance } from "../appearance";
import {
  getBlurEnabled,
  getCameraEnabled,
  getMicEnabled,
  getRememberedName,
  getUserKey,
  setBlurEnabled,
  setCameraEnabled,
  setMicEnabled,
  setRememberedName,
} from "../lib/storage";
import { mediaProblemText, openCallMedia, openCamera, stopStream, type CallMedia } from "../lib/media";
import { api } from "../lib/api";
import { BlurLoading } from "../components/BlurLoading";
import { blurSupported, createBlur, warmBlur, type BlurHandle } from "../lib/blur";
import { VideoView } from "../components/VideoView";
import { AccentSwatches } from "../components/AccentPicker";
import { LegalLinks } from "../components/LegalLinks";
import { CameraIcon, CameraOffIcon, CheckIcon, GearIcon, HourglassIcon, MicIcon, MicOffIcon, RecordIcon } from "../components/icons";
import { WaitingLine, waitingText } from "../components/WaitingLine";
import { WaitingTrivia } from "../components/WaitingTrivia";
import { CallMenu, ControlsTray } from "../components/ControlsTray";

interface PreJoinScreenProps {
  /** `record`: the owner pressed Record before joining. */
  readonly onJoin: (name: string, record: boolean) => void;
  /** The owner's own room: show the Record button. */
  readonly canRecord?: boolean;
  /** This account is in the call right now on another device: Join becomes
   * Move to this device. */
  readonly onMove?: (name: string) => void;
  /** The owner's own room: offer a preview of what a guest sees while they
   * wait to be let in. Nothing is sent; no knock reaches the room. */
  readonly waitingPreview?: { readonly hostName: string | null; readonly trivia: boolean };
}

const APPEARANCE_NAMES: Record<Appearance, string> = { light: "Light", dark: "Dark", auto: "Auto" };

type MediaState = { readonly phase: "asking" } | { readonly phase: "done"; readonly media: CallMedia };

export function PreJoinScreen({ onJoin, onMove, canRecord = false, waitingPreview }: PreJoinScreenProps): JSX.Element {
  const [previewOpen, setPreviewOpen] = useState(false);
  // Off every time, never remembered: a call starts unrecorded unless the
  // owner asks for this one.
  const [recordOn, setRecordOn] = useState(false);
  const [name, setName] = useState(getRememberedName());
  const [micOn, setMicOn] = useState(getMicEnabled());
  const [cameraOn, setCameraOn] = useState(getCameraEnabled());
  const [media, setMedia] = useState<MediaState>({ phase: "asking" });
  const [settingsOpen, setSettingsOpen] = useState(false);

  // The popup holds the page still while it is open.
  useEffect(() => {
    if (!settingsOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const before = { html: html.style.overflow, body: body.style.overflow };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = before.html;
      body.style.overflow = before.body;
    };
  }, [settingsOpen]);
  const canBlur = blurSupported();
  const [blurOn, setBlurOn] = useState(() => getBlurEnabled() && canBlur);
  const [blurStream, setBlurStream] = useState<MediaStream | null>(null);
  const [blurNote, setBlurNote] = useState<string | null>(null);
  const mediaRef = useRef<CallMedia | null>(null);
  const aliveRef = useRef(true);

  const ask = async (): Promise<void> => {
    setMedia({ phase: "asking" });
    stopStream(mediaRef.current?.stream);
    const got = await openCallMedia();
    if (!aliveRef.current) {
      stopStream(got.stream);
      return;
    }
    if (got.audioTrack) got.audioTrack.enabled = getMicEnabled();
    // A camera that is off is closed, not just hidden, so its light is out.
    if (got.videoTrack && !getCameraEnabled()) got.videoTrack.stop();
    mediaRef.current = got;
    setMedia({ phase: "done", media: got });
  };

  useEffect(() => {
    aliveRef.current = true;
    void ask();
    return () => {
      aliveRef.current = false;
      stopStream(mediaRef.current?.stream);
    };
    // Once per mount; `ask` is also the Try again button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The segmenter loads while he looks at the preview, so turning blur on
  // does not wait on it.
  useEffect(() => {
    warmBlur(getBlurEnabled());
  }, []);

  // The preview draws the blur from the camera track while blur is on. A
  // failure turns the choice back off and says so.
  const rawTrack = media.phase === "done" && cameraOn ? media.media.videoTrack : null;
  useEffect(() => {
    if (!blurOn || !rawTrack) {
      setBlurStream(null);
      return;
    }
    let cancelled = false;
    let handle: BlurHandle | null = null;
    createBlur(rawTrack).then(
      (h) => {
        if (cancelled) {
          h.stop();
          return;
        }
        handle = h;
        setBlurStream(new MediaStream([h.track]));
      },
      (err: unknown) => {
        if (cancelled) return;
        console.error("[blur] pre-join blur failed", err);
        setBlurOn(false);
        setBlurEnabled(false);
        setBlurNote("Background blur is not available here.");
      },
    );
    return () => {
      cancelled = true;
      handle?.stop();
      setBlurStream(null);
    };
  }, [blurOn, rawTrack]);

  const toggleBlur = (): void => {
    const next = !blurOn;
    setBlurNote(null);
    setBlurOn(next);
    setBlurEnabled(next);
  };

  const toggleMic = (): void => {
    const next = !micOn;
    setMicOn(next);
    setMicEnabled(next);
    if (mediaRef.current?.audioTrack) mediaRef.current.audioTrack.enabled = next;
  };

  const toggleCamera = (): void => {
    const next = !cameraOn;
    const current = mediaRef.current;
    if (!next) {
      setCameraOn(false);
      setCameraEnabled(false);
      current?.videoTrack?.stop();
      return;
    }
    if (!current?.videoTrack || current.videoTrack.readyState !== "ended") {
      setCameraOn(true);
      setCameraEnabled(true);
      return;
    }
    // The camera was closed while it was off; open it again.
    void openCamera({ video: true, audio: false }).then(
      (opened) => {
        const track = opened.videoTrack;
        if (!track || !aliveRef.current || mediaRef.current !== current) {
          stopStream(opened.stream);
          return;
        }
        const stream = new MediaStream([...current.stream.getAudioTracks(), track]);
        const reopened: CallMedia = { ...current, stream, videoTrack: track };
        mediaRef.current = reopened;
        setMedia({ phase: "done", media: reopened });
        setCameraOn(true);
        setCameraEnabled(true);
      },
      () => {},
    );
  };

  const handleJoin = (): void => {
    setRememberedName(name);
    stopStream(mediaRef.current?.stream);
    mediaRef.current = null;
    if (onMove) onMove(name);
    else onJoin(name, canRecord && recordOn);
  };

  // A signed-in person's color is kept on their account too, so it follows
  // them to their other devices.
  const saveAccent = (hue: AccentHue | null): void => {
    if (getUserKey()) void api("/api/me", { method: "PATCH", body: { settings: { accent: hue } } }).catch(() => {});
  };

  const [appearance, setAppearance] = useState<Appearance>(getStoredAppearance());
  const chooseAppearance = (value: Appearance): void => {
    setAppearance(value);
    setStoredAppearance(value);
    applyAppearance(value);
    if (getUserKey()) void api("/api/me", { method: "PATCH", body: { settings: { appearance: value } } }).catch(() => {});
  };

  const got = media.phase === "done" ? media.media : null;
  const problem = got ? mediaProblemText(got) : null;
  const blocked = got?.cameraProblem === "denied" || got?.micProblem === "denied";
  const showVideo = !!got?.videoTrack && cameraOn;

  return (
    <div className={onMove ? "prejoin prejoin--elsewhere" : "prejoin"}>
      {onMove ? (
        <p className="prejoin__elsewhere" role="status">
          You're in this call on another device.
        </p>
      ) : null}

      <div className="prejoin__stage">
        <div className="prejoin__frame">
          {showVideo && got ? (
            <>
              <VideoView stream={blurOn && blurStream ? blurStream : got.stream} mirrored muted fit="cover" />
              <BlurLoading show={blurOn && !blurStream && !blurNote} />
            </>
          ) : (
            <div className="prejoin__placeholder">
              {media.phase === "asking" ? (
                <p className="prejoin__message">Allow your camera and mic when your browser asks.</p>
              ) : got?.videoTrack || !got?.cameraProblem ? (
                <>
                  <div className="prejoin__initial">{(name.trim()[0] ?? "?").toUpperCase()}</div>
                  <span className="prejoin__caption">
                    <CameraOffIcon size={16} />
                    Camera off
                  </span>
                </>
              ) : (
                <>
                  <p className="prejoin__message">{problem}</p>
                  {blocked ? (
                    <button type="button" className="btn" onClick={() => void ask()} style={{ height: "var(--tap-min)" }}>
                      Try again
                    </button>
                  ) : null}
                </>
              )}
            </div>
          )}
          {/* The camera works but the mic does not: say so over the video. */}
          {showVideo && (problem || blurNote) ? <div className="prejoin__banner">{problem ?? blurNote}</div> : null}
        </div>
      </div>

      <div className="prejoin__bar">
        <div className="prejoin__toggles">
          <button type="button" className={micOn ? "btn" : "btn btn--off"} onClick={toggleMic} aria-pressed={micOn}>
            {micOn ? <MicIcon size={18} /> : <MicOffIcon size={18} />}
            {micOn ? "Mic on" : "Mic off"}
          </button>
          <button type="button" className={cameraOn ? "btn" : "btn btn--off"} onClick={toggleCamera} aria-pressed={cameraOn}>
            {cameraOn ? <CameraIcon size={18} /> : <CameraOffIcon size={18} />}
            {cameraOn ? "Camera on" : "Camera off"}
          </button>
          {canRecord && !onMove ? (
            <button
              type="button"
              className={recordOn ? "btn btn--icon btn--rec" : "btn btn--icon"}
              onClick={() => setRecordOn((v) => !v)}
              aria-pressed={recordOn}
              aria-label="Record this call"
              title={recordOn ? "Recording starts once someone joins" : "Record this call"}
              data-testid="prejoin-record"
            >
              <RecordIcon size={19} />
            </button>
          ) : null}
          {waitingPreview && !onMove ? (
            <button
              type="button"
              className="btn prejoin__wbtn"
              onClick={() => setPreviewOpen(true)}
              aria-label="Preview the waiting room"
              title="Preview the waiting room"
              data-testid="prejoin-waiting-preview"
            >
              <HourglassIcon size={19} />
              <span className="prejoin__wlabel">Waiting room</span>
            </button>
          ) : null}
          <button type="button" className="btn btn--icon" onClick={() => setSettingsOpen(true)} aria-label="Settings">
            <GearIcon size={19} />
          </button>
        </div>

        <div className="prejoin__join">
          <label htmlFor="display-name" className="prejoin__label">
            Your name
          </label>
          <input
            id="display-name"
            className="input"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleJoin();
            }}
            maxLength={60}
            autoComplete="name"
          />
          <button type="button" className="btn btn--primary prejoin__go" onClick={handleJoin}>
            {onMove ? "Move to this device" : "Join"}
          </button>
        </div>
      </div>

      {previewOpen && waitingPreview ? (
        <div
          className="wpreview"
          role="dialog"
          aria-label="Waiting room preview"
          data-testid="waiting-preview"
          tabIndex={-1}
          ref={(el) => {
            if (el && !el.contains(document.activeElement)) el.focus();
          }}
          onKeyDown={(e) => e.key === "Escape" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) && setPreviewOpen(false)}
        >
          {showVideo && got ? (
            <>
              <VideoView stream={blurOn && blurStream ? blurStream : got.stream} mirrored muted fit="cover" />
              <BlurLoading show={blurOn && !blurStream && !blurNote} />
            </>
          ) : (
            <div className="wpreview__initial">{(name.trim()[0] ?? "?").toUpperCase()}</div>
          )}
          <WaitingLine text={waitingText(waitingPreview.hostName)} />
          {/* His own preview: the pencil on the card opens the question editor (QUEUE row 431). */}
          {waitingPreview.trivia ? <WaitingTrivia editable /> : null}
          {/* The guest's own controls, live: mic and camera act on this page, the
              "..." holds blur, and hanging up is how a guest leaves the waiting
              room, so here it closes the preview. */}
          <CallMenu canFlipCamera={false} canShareScreen={false} sharingScreen={false} originalSoundOn={false} blurOn={canBlur ? blurOn : undefined} onToggleBlur={canBlur ? toggleBlur : undefined} />
          <ControlsTray micOn={micOn} cameraOn={cameraOn} onToggleMic={toggleMic} onToggleCamera={toggleCamera} onLeave={() => setPreviewOpen(false)} />
        </div>
      ) : null}

      {settingsOpen ? (
        <div className="scrim" onClick={() => setSettingsOpen(false)} onKeyDown={(e) => e.key === "Escape" && setSettingsOpen(false)}>
          <div
            className="modal"
            role="dialog"
            aria-label="Settings"
            onClick={(event) => event.stopPropagation()}
            style={{ padding: "var(--space-5) var(--space-4)", gap: "var(--space-4)" }}
          >
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 650 }}>Settings</h2>
            <div className="section">
              <h3 className="section__heading">Color</h3>
              <AccentSwatches onChange={saveAccent} />
            </div>
            <div className="section">
              <h3 className="section__heading" id="pj-theme-label">
                Theme
              </h3>
              <div className="seg" role="radiogroup" aria-labelledby="pj-theme-label">
                {APPEARANCES.map((value) => (
                  <button key={value} type="button" role="radio" aria-checked={appearance === value} className="seg__option" style={{ minHeight: "var(--tap-min)" }} onClick={() => chooseAppearance(value)}>
                    {APPEARANCE_NAMES[value]}
                  </button>
                ))}
              </div>
            </div>
            {canBlur ? (
              <div className="section">
                <button type="button" role="switch" aria-checked={blurOn} className="pickrow" onClick={toggleBlur}>
                  <span className="pickrow__box" aria-hidden="true">
                    {blurOn ? <CheckIcon size={14} /> : null}
                  </span>
                  <span className="pickrow__text">
                    <span className="pickrow__name">Blur background</span>
                  </span>
                </button>
                {blurNote ? <p className="field__error">{blurNote}</p> : null}
              </div>
            ) : null}
            <button type="button" className="btn btn--primary" onClick={() => setSettingsOpen(false)} style={{ height: "var(--tap-min)" }}>
              Done
            </button>
            <LegalLinks />
          </div>
        </div>
      ) : null}
    </div>
  );
}
